import {
  FlexBox,
  JoonUIColor,
  SPACING,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import { CATEGORY_COLORS, TOOLS, ToolMeta } from "./sharedTools"
import PageContentWrapper from "../../components/layout/PageContentWrapper"
import PageWrapper from "../../components/pageWrapper/PageWrapper"
import { requireAuth } from "../../util/auth"

const ToolsIndex = requireAuth(() => {
  const [search, setSearch] = useState("")

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return TOOLS
    return TOOLS.filter((t) =>
      [t.name, t.description, t.category].some((s) =>
        s.toLowerCase().includes(q)
      )
    )
  }, [search])

  return (
    <PageWrapper style={{ background: JoonUIColor.background.xlightGray }}>
      <PageContentWrapper>
        <FlexBox direction="column" gap={SPACING.space2}>
          <Typography variant="h3" textAlign="left">
            Free helpful AI tools for BCBAs
          </Typography>
          <Typography variant="bodySmall" textAlign="left">
            Speed up day-to-day tasks, save time, and get fresh ideas.
            Try these AI tools to better support your clients and their
            caregivers.
          </Typography>
        </FlexBox>
        <div style={{ maxWidth: 480 }}>
          <TextInput
            name="Search tools"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tools by name, description, or category…"
            icon="search"
            fullWidth
          />
        </div>
        {filtered.length === 0 ? (
          <FlexBox justify="center" style={{ padding: SPACING.space8 }}>
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              No tools match "{search}".
            </Typography>
          </FlexBox>
        ) : (
          <div className="tools-grid">
            {filtered.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>
        )}
      </PageContentWrapper>
    </PageWrapper>
  )
})

const ToolCard = ({ tool }: { tool: ToolMeta }) => {
  const navigate = useNavigate()
  const colors = CATEGORY_COLORS[tool.category]
  return (
    <button
      onClick={() => navigate(tool.path)}
      style={{
        textAlign: "left",
        background: JoonUIColor.background.primaryNeutral,
        border: `1px solid ${JoonUIColor.border.default}`,
        borderRadius: SPACING.space2,
        padding: 0,
        cursor: "pointer",
        transition: "border-color 0.15s, transform 0.15s",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = JoonUIColor.semantic.primary
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = JoonUIColor.border.default
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 10",
          background: JoonUIColor.background.lightGray,
          overflow: "hidden",
        }}
      >
        <img
          src={tool.image}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        <span
          style={{
            position: "absolute",
            left: SPACING.space3,
            bottom: -SPACING.space2 as any,
            background: colors.background,
            color: colors.text,
            fontSize: 12,
            fontWeight: 600,
            padding: `${SPACING.space1} ${SPACING.space3}`,
            borderRadius: 999,
            whiteSpace: "nowrap",
            transform: "translateY(50%)",
          }}
        >
          {tool.category}
        </span>
      </div>
      <FlexBox
        direction="column"
        gap={SPACING.space2}
        style={{
          padding: `${SPACING.space5} ${SPACING.space4} ${SPACING.space4}`,
        }}
      >
        <Typography variant="bodyBold" textAlign="left">
          {tool.name}
        </Typography>
        <Typography
          variant="bodySmall"
          textAlign="left"
          color={JoonUIColor.text.secondary}
        >
          {tool.description}
        </Typography>
      </FlexBox>
    </button>
  )
}

export default ToolsIndex
